import {
  DownloadIcon,
  PencilAltIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { useMutation } from '@tanstack/react-query';
import useLocale from 'next-translate/useTranslation';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Image as ImageType } from 'types';

import clsxm from '@/lib/clsxm';
import uuidv4 from '@/lib/uuid';

import {
  GPT_IMAGE_2_EDIT_ASPECTS,
  DEFAULT_GPT_IMAGE_MODEL,
  isGptImage2FamilyModel,
} from '@/constants/gptImageModels';
import { editModels } from '@/constants/imageModels';
import axiosInstance from '@/utils/httpClient';
import { createClient as createComponentClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';

import ClickToAnnotate from './ClickToAnnotate';
import { compileAnnotationsToPrompt } from './ClickToAnnotate/utils/promptCompiler';
import Canvas from './edit/Canvas';
import PromptForm from './edit/PromptForm';
import ImageAnnotator from './ImageAnnotator';
import Modal from '../atoms/Modal';
import Spinner from '../Spinner';

interface EditModalProps {
  image: Partial<ImageType>;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (updatedImage: Partial<ImageType>, prompt: string) => void;
}

const EditModal = ({ image, isOpen, onClose, onUpdate }: EditModalProps) => {
  const { t } = useLocale('ai-images');
  const { isFreePlan } = useUser();
  const supabaseClient = createComponentClient();
  const [error, setError] = useState<string | null>(null);
  const [maskImage, setMaskImage] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<string>(
    image.image_url || '',
  );
  // Image history: array of image URLs, first one is always the original
  const [imageHistory, setImageHistory] = useState<string[]>([
    image.image_url || '',
  ]);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
  const [clearCanvasCounter, setClearCanvasCounter] = useState(0);
  const [isCanvasMode] = useState(false); // Canvas mode is currently disabled
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [dimensions, setDimensions] = useState<string>('auto');
  const [currentEditModel, setCurrentEditModel] = useState<string>(
    DEFAULT_GPT_IMAGE_MODEL,
  );
  const [resolution, setResolution] = useState<string | null>(null);
  // Track the original image URL to detect when modal is opened with a different image
  const originalImageRef = useRef<string>(image.image_url || '');

  // Get the selected edit model definition
  const selectedEditModelDefinition = useMemo(
    () => editModels.find((item) => item.value === currentEditModel),
    [currentEditModel],
  );

  // Calculate available resolutions based on the selected edit model
  const availableResolutions = useMemo(
    () => selectedEditModelDefinition?.resolutions || [],
    [selectedEditModelDefinition],
  );
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [annotatedImage, setAnnotatedImage] = useState<string | null>(null);
  const [annotationMode, setAnnotationMode] = useState<'draw' | 'click' | null>(
    null,
  );
  const [editMode, setEditMode] = useState<'prompt' | 'visual'>('prompt');
  const [visualMarkers, setVisualMarkers] = useState<any[]>([]);
  const [hasValidAnnotations, setHasValidAnnotations] = useState(false);
  const [annotationResetKey, setAnnotationResetKey] = useState(0);
  const compiledPromptRef = useRef<string>('');

  // Reset state only when modal is opened with a truly different image
  useEffect(() => {
    // Only reset if the image prop changed to a different original image
    if (image.image_url && image.image_url !== originalImageRef.current) {
      originalImageRef.current = image.image_url;
      setError(null);
      setMaskImage(null);
      setCurrentImage(image.image_url);
      setImageHistory([image.image_url]);
      setSelectedHistoryIndex(0);
      setAdditionalImages([]);
      setDimensions('auto');
      setShowAnnotator(false);
      setAnnotatedImage(null);
      setAnnotationMode(null);
      setEditMode('prompt');
      setVisualMarkers([]);
      setHasValidAnnotations(false);
    }
  }, [image.image_url]);

  // Reset dimensions when edit model changes if necessary
  useEffect(() => {
    // When switching to OpenAI GPT Image 1 or 1.5, reset to valid dimension
    if (
      (currentEditModel === 'OpenAI GPT Image 1' ||
        currentEditModel === 'OpenAI GPT Image 1.5') &&
      dimensions !== '1024x1024' &&
      dimensions !== '1536x1024' &&
      dimensions !== '1024x1536'
    ) {
      setDimensions('auto');
    }
    // When switching to a GPT Image 2 / 2.5 tier, reset to a valid dimension
    if (
      isGptImage2FamilyModel(currentEditModel) &&
      !GPT_IMAGE_2_EDIT_ASPECTS.includes(dimensions)
    ) {
      setDimensions('auto');
    }
    // When switching to Nano Banana 2 or Nano Banana Pro, reset to valid dimension if current is not supported
    if (
      (currentEditModel === 'Nano Banana 2' ||
        currentEditModel === 'Nano Banana 2.1' ||
        currentEditModel === 'Nano Banana 2 Lite' ||
        currentEditModel === 'Nano Banana Pro') &&
      dimensions !== 'auto' &&
      dimensions !== '1:1' &&
      dimensions !== '2:3' &&
      dimensions !== '3:2' &&
      dimensions !== '3:4' &&
      dimensions !== '4:3' &&
      dimensions !== '4:5' &&
      dimensions !== '5:4' &&
      dimensions !== '9:16' &&
      dimensions !== '16:9' &&
      dimensions !== '21:9'
    ) {
      setDimensions('auto');
    }
  }, [currentEditModel, dimensions]);

  // Handle resolution changes when availableResolutions changes
  useEffect(() => {
    if (availableResolutions.length > 0) {
      const defaultResolution = availableResolutions.includes('2K')
        ? '2K'
        : availableResolutions[0];

      setResolution((current) =>
        current && availableResolutions.includes(current)
          ? current
          : defaultResolution,
      );
    } else {
      setResolution(null);
    }
  }, [availableResolutions]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const pollForImage = async (imageId: number) => {
    try {
      const response = await axiosInstance.post('/api/images', {
        action: 'GET',
        image_id: imageId,
      });

      if (response.data.image && response.data.image.image_url) {
        // Image is ready - add to history
        const newImageUrl = response.data.image.image_url;
        setImageHistory((prev) => [...prev, newImageUrl]);
        setSelectedHistoryIndex((prev) => prev + 1);
        setCurrentImage(newImageUrl);
        onUpdate?.({ image_url: newImageUrl }, '');
        setIsPolling(false);
        // Clear annotation state when we get the new image
        setAnnotatedImage(null);
        setShowAnnotator(false);

        // Clear the polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Error polling for image:', error);
      return false;
    }
  };

  const handleFileUpload = (file: File) => {
    setIsFileLoading(true);

    if (!file) {
      setIsFileLoading(false);
      return;
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `upscale/${fileName}`;

    // Store the blob URL temporarily
    const blobUrl = URL.createObjectURL(file);
    setAdditionalImages((images) => [...images, blobUrl]);

    supabaseClient.storage
      .from('uploads')
      .upload(filePath, file)
      .then(({ error }) => {
        if (error) {
          console.log(error);
          // Remove the blob URL if upload failed
          setAdditionalImages((images) =>
            images.filter((img) => img !== blobUrl),
          );
          setIsFileLoading(false);
          return;
        }

        const fileData = supabaseClient.storage
          .from('uploads')
          .getPublicUrl(filePath);

        const publicUrl = fileData.data.publicUrl as string;

        // Replace the blob URL with the public URL
        setAdditionalImages((images) =>
          images.map((img) => (img === blobUrl ? publicUrl : img)),
        );

        setIsFileLoading(false);
      });
  };

  const [isUploading, setIsUploading] = useState(false);

  const mutation = useMutation(
    (data: {
      prompt: string;
      image: string;
      mask?: string;
      useHD?: string;
      editModel?: string;
      additionalImages?: string[];
      aspectRatio?: string;
      resolution?: string;
    }) => {
      setClearCanvasCounter((prev) => prev + 1);
      setError(null);
      setMaskImage(null);

      // Validate that no blob URLs are present
      const allImages = data.additionalImages
        ? [currentImage, ...data.additionalImages]
        : [data.image];
      const hasBlobUrls = allImages.some((url) => url?.startsWith('blob:'));

      if (hasBlobUrls) {
        throw new Error('Please wait for all images to finish uploading');
      }

      // If there are additional images, send all images in the images field
      const requestData: any = {
        action: isCanvasMode ? 'UPDATE' : 'UPDATE_KONTEXT',
        image_id: image.id,
        prompt: data.prompt,
        useHD: data.useHD,
        aspectRatio: data.aspectRatio || dimensions,
        ...(data.mask && { mask: data.mask }),
        ...(data.editModel && { editModel: data.editModel }),
        ...(data.resolution && { resolution: data.resolution }),
      };

      // For multiple images, send them as a JSON string array in the images field
      // and use the first image as the primary image
      if (data.additionalImages && data.additionalImages.length > 0) {
        requestData.images = JSON.stringify([
          data.image, // This is already the processed annotated image or current image
          ...data.additionalImages,
        ]);
        requestData.image = data.image;
      } else {
        requestData.image = data.image;
      }

      return axiosInstance.post('/api/images', requestData);
    },
    {
      onSuccess: (response, data) => {
        const isAsyncModel =
          data.editModel === 'Qwen Image' ||
          data.editModel === 'FLUX.2 [max]' ||
          data.editModel === 'FLUX.2 [pro]' ||
          data.editModel === 'FLUX.2 [flex]' ||
          data.editModel === 'Seedream 4.5' ||
          data.editModel === 'Seedream 5.0 Lite' ||
          data.editModel === 'Seedream 5.0 Pro' ||
          data.editModel === 'Seedream 4.0' ||
          data.editModel === 'Ideogram v3 Character' ||
          data.editModel === 'Reve Fast' ||
          data.editModel === 'Reve' ||
          data.editModel === 'Nano Banana 2' ||
          data.editModel === 'Nano Banana 2.1' ||
          data.editModel === 'Nano Banana 2 Lite' ||
          data.editModel === 'Nano Banana Pro';

        // Clear annotation state when we get a new image
        setAnnotatedImage(null);
        setShowAnnotator(false);
        setAnnotationMode(null);
        setVisualMarkers([]);
        setHasValidAnnotations(false);
        setAnnotationResetKey((prev) => prev + 1);

        if (isCanvasMode) {
          const newImageUrl = response.data.image || '';
          setImageHistory((prev) => [...prev, newImageUrl]);
          setSelectedHistoryIndex((prev) => prev + 1);
          setCurrentImage(newImageUrl);
          onUpdate?.({ image_url: newImageUrl }, '');
        } else {
          const resultImage = response.data.images[0];

          // Check for async models including fallback to SeededEdit
          const needsPolling =
            (isAsyncModel ||
              resultImage?.model === 'SeededEdit' ||
              resultImage?.fal_request_id) &&
            resultImage?.id &&
            !resultImage?.image_url;

          if (needsPolling) {
            // Start polling for async models
            setIsPolling(true);

            let pollCount = 0;
            const maxPolls = 30; // Maximum 60 seconds (30 * 2 seconds)

            // Poll every 2 seconds
            pollingIntervalRef.current = setInterval(async () => {
              pollCount++;
              const isReady = await pollForImage(resultImage.id);

              if (isReady || pollCount >= maxPolls) {
                setIsPolling(false);

                if (pollCount >= maxPolls && !isReady) {
                  setError(
                    'Image processing is taking longer than expected. Please try again.',
                  );
                }

                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
              }
            }, 5000);

            // Also do an immediate check
            pollForImage(resultImage.id);
          } else if (resultImage?.image_url) {
            // Sync models - image is ready immediately, add to history
            setImageHistory((prev) => [...prev, resultImage.image_url]);
            setSelectedHistoryIndex((prev) => prev + 1);
            setCurrentImage(resultImage.image_url);
            onUpdate?.({ image_url: resultImage.image_url }, '');
          } else {
            // Handle case where no image URL is returned
            setError('Failed to generate image. Please try again.');
          }
        }
      },
      onError: (error) => {
        setError('Failed to update image');
        console.error(error);
        setIsPolling(false);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      },
    },
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const useHD = formData.get('useHD') === '1';
    const editModel = formData.get('editModel') as string;

    // Use compiled prompt from markers in visual mode, otherwise use form prompt
    const prompt =
      editMode === 'visual'
        ? compileAnnotationsToPrompt(visualMarkers)
        : (formData.get('prompt') as string);

    if (!prompt.trim()) {
      if (editMode === 'visual') {
        toast.error(t('Add descriptions to your markers to generate changes'));
      } else {
        toast.error(t('Please enter a prompt'));
      }
      return;
    }

    if (isCanvasMode && !maskImage) {
      toast.error(
        t('Please draw on the image to highlight the area you want to edit'),
      );
      return;
    }

    // Handle annotated image if it's a blob URL
    let processedAnnotatedImage = annotatedImage;
    if (annotatedImage && annotatedImage.startsWith('blob:')) {
      setIsUploading(true);
      try {
        // Convert blob URL to file and upload
        const response = await fetch(annotatedImage);
        const blob = await response.blob();
        const file = new File([blob], 'annotated-image.png', {
          type: 'image/png',
        });

        // Upload to Supabase
        const fileExt = 'png';
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `upscale/${fileName}`;

        const { error } = await supabaseClient.storage
          .from('uploads')
          .upload(filePath, file);

        if (!error) {
          const fileData = supabaseClient.storage
            .from('uploads')
            .getPublicUrl(filePath);

          processedAnnotatedImage = fileData.data.publicUrl;
          // Update state with the uploaded URL
          setAnnotatedImage(processedAnnotatedImage);
        } else {
          console.error('Failed to upload annotated image:', error);
          toast.error(t('Failed to upload annotated image'));
          setIsUploading(false);
          return;
        }
      } catch (error) {
        console.error('Error processing annotated image:', error);
        toast.error(t('Failed to process annotated image'));
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const data = {
      prompt,
      image: processedAnnotatedImage || currentImage,
      ...(isCanvasMode && maskImage && { mask: maskImage }),
      ...(isCanvasMode ? {} : { editModel }),
      useHD: useHD.toString(),
      ...(additionalImages.length > 0 && { additionalImages }),
      aspectRatio: dimensions,
      ...(resolution && { resolution }),
    };

    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      setError('Failed to update image');
    }
  };

  // Handle selecting an image from history
  const handleSelectHistoryImage = (index: number) => {
    if (mutation.isLoading || isPolling) return;
    setSelectedHistoryIndex(index);
    setCurrentImage(imageHistory[index]);
    setAnnotatedImage(null);
    // Reset annotation markers when switching images
    setVisualMarkers([]);
    setHasValidAnnotations(false);
    setAnnotationResetKey((prev) => prev + 1);
  };

  return (
    <Modal
      showModal={isOpen}
      onClose={onClose}
      doNotCloseOnOverlayClick={true}
      className={clsxm(
        'mt-5 overflow-y-auto h-[95svh]',
        showAnnotator || annotationMode === 'click'
          ? 'sm:max-w-[95%]'
          : 'sm:max-w-[80%]',
      )}
    >
      <main
        className={clsxm(
          'mx-auto',
          (showAnnotator || annotationMode === 'click') && 'h-full',
        )}
      >
        <div
          className={clsxm(
            'px-6 py-8 lg:px-8 transition-all duration-300',
            showAnnotator || annotationMode === 'click'
              ? 'h-full flex flex-col'
              : 'grid grid-cols-1 gap-x-12 md:grid-cols-3',
          )}
        >
          {/* Left column - Actions and controls */}
          {!showAnnotator && annotationMode !== 'click' && (
            <div className='flex flex-col space-y-6 mb-4 sm:mb-0'>
              {/* Header */}
              <h2 className='text-2xl font-semibold tracking-tight text-gray-900 dark:text-white'>
                {t('Edit Image')}
              </h2>

              {/* Error Alert */}
              {error && (
                <div className='animate-in slide-in-from-top-2 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20'>
                  <div className='flex'>
                    <div className='flex-shrink-0'>
                      <svg
                        className='h-5 w-5 text-red-400'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                    <div className='ml-3'>
                      <p className='text-sm font-medium text-red-800 dark:text-red-400'>
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Prompt Form with enhanced styling */}
              <div className='space-y-2'>
                {/* Show warning if blob URLs exist */}
                {additionalImages.some((url) => url?.startsWith('blob:')) && (
                  <div className='rounded-lg border border-yellow-200 bg-yellow-50 p-2 dark:border-yellow-900/50 dark:bg-yellow-900/20'>
                    <p className='text-xs text-yellow-800 dark:text-yellow-400'>
                      ⏳ {t('Uploading images, please wait...')}
                    </p>
                  </div>
                )}
                <PromptForm
                  onSubmit={handleSubmit}
                  initialPrompt=''
                  isFreePlan={isFreePlan}
                  isGenerating={
                    mutation.isLoading ||
                    isPolling ||
                    isFileLoading ||
                    isUploading ||
                    additionalImages.some((url) => url?.startsWith('blob:'))
                  }
                  isCanvasMode={isCanvasMode}
                  onModelChange={setCurrentEditModel}
                  hasReferenceImages={additionalImages.length > 0}
                  resolution={resolution}
                  additionalImages={additionalImages}
                  onAddImages={(files) => {
                    files.forEach((file) => handleFileUpload(file));
                  }}
                  onRemoveImage={(index) => {
                    setAdditionalImages((images) =>
                      images.filter((_, i) => i !== index),
                    );
                  }}
                  isFileLoading={isFileLoading}
                  dimensions={dimensions}
                  onDimensionsChange={setDimensions}
                  dimensionOptions={(() => {
                    // Determine dimensions options based on edit model
                    if (currentEditModel === 'Nano Banana') {
                      return [
                        'auto',
                        '1:1',
                        '2:3',
                        '3:2',
                        '3:4',
                        '4:3',
                        '4:5',
                        '5:4',
                        '9:16',
                        '16:9',
                        '21:9',
                      ];
                    } else if (
                      currentEditModel === 'Nano Banana 2' ||
                      currentEditModel === 'Nano Banana 2.1' ||
                      currentEditModel === 'Nano Banana 2 Lite' ||
                      currentEditModel === 'Nano Banana Pro'
                    ) {
                      return [
                        'auto',
                        '1:1',
                        '2:3',
                        '3:2',
                        '3:4',
                        '4:3',
                        '4:5',
                        '5:4',
                        '9:16',
                        '16:9',
                        '21:9',
                      ];
                    } else if (
                      currentEditModel === 'Qwen Image' ||
                      currentEditModel === 'FLUX.2 [max]' ||
                      currentEditModel === 'FLUX.2 [pro]' ||
                      currentEditModel === 'FLUX.2 [flex]' ||
                      currentEditModel === 'Seedream 4.5' ||
                      currentEditModel === 'Seedream 5.0 Lite' ||
                      currentEditModel === 'Seedream 5.0 Pro' ||
                      currentEditModel === 'Seedream 4.0' ||
                      currentEditModel === 'Ideogram v3 Character'
                    ) {
                      return ['1:1', '3:4', '4:3', '9:16', '16:9'];
                    } else if (
                      currentEditModel === 'OpenAI GPT Image 1' ||
                      currentEditModel === 'OpenAI GPT Image 1.5'
                    ) {
                      return ['auto', '1024x1024', '1536x1024', '1024x1536'];
                    } else if (isGptImage2FamilyModel(currentEditModel)) {
                      return GPT_IMAGE_2_EDIT_ASPECTS;
                    } else if (
                      currentEditModel === 'Grok' ||
                      currentEditModel === 'Grok Pro' ||
                      currentEditModel === 'Grok Imagine 2.0' ||
                      currentEditModel === 'Muse Image'
                    ) {
                      return [
                        'auto',
                        '1:1',
                        '3:4',
                        '4:3',
                        '9:16',
                        '16:9',
                        '2:3',
                        '3:2',
                      ];
                    } else if (
                      currentEditModel === 'Reve Fast' ||
                      currentEditModel === 'Reve'
                    ) {
                      return ['auto'];
                    } else {
                      // Flux Kontext Pro/Max - full options
                      return [
                        'auto',
                        '1:1',
                        '21:9',
                        '16:9',
                        '4:3',
                        '3:2',
                        '2:3',
                        '3:4',
                        '9:16',
                        '9:21',
                      ];
                    }
                  })()}
                  resolutionValue={
                    resolution && availableResolutions.includes(resolution)
                      ? resolution
                      : availableResolutions[0]
                  }
                  onResolutionChange={setResolution}
                  resolutionOptions={availableResolutions}
                  editMode={editMode}
                  onEditModeChange={setEditMode}
                  hasValidAnnotations={hasValidAnnotations}
                />
              </div>

              {/* Edit Mode Section
            <div className='mb-4 sm:mb-0 space-y-4'>
              <div className='relative rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 transition-all hover:border-gray-300 dark:border-gray-700 dark:from-gray-800 dark:to-gray-900 dark:hover:border-gray-600'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1 pr-3'>
                    <h3 className='text-sm font-semibold text-gray-900 dark:text-white'>
                      {isCanvasMode ? t('Precision Editing') : t('Quick Edit')}
                    </h3>
                    <p className='mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400'>
                      {isCanvasMode
                        ? t(
                            'Draw masks to target specific areas for precise changes',
                          )
                        : t('Describe changes in text for instant AI editing')}
                    </p>
                  </div>
                  <div className='flex-shrink-0'>
                    <Toggle
                      isSmall={true}
                      initialValue={isCanvasMode}
                      onChange={(enabled) => {
                        setIsCanvasMode(enabled);
                        if (!enabled) {
                          setMaskImage(null);
                          setClearCanvasCounter((prev) => prev + 1);
                        }
                      }}
                      disabled={mutation.isLoading || isPolling}
                    />
                  </div>
                </div>
              </div>
            </div> */}

              {/* Image History Section - only show when there are edits */}
              {imageHistory.length > 1 && (
                <div className='space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700'>
                  <div className='flex items-center gap-2 overflow-x-auto py-2 px-2'>
                    {imageHistory.map((imgUrl, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectHistoryImage(index)}
                        disabled={mutation.isLoading || isPolling}
                        className={clsxm(
                          'relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-200',
                          selectedHistoryIndex === index
                            ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-gray-900'
                            : 'ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600',
                          (mutation.isLoading || isPolling) &&
                            'opacity-50 cursor-not-allowed',
                        )}
                      >
                        <img
                          src={imgUrl}
                          alt={index === 0 ? 'Original' : `Edit ${index}`}
                          className='w-full h-full object-cover'
                        />
                        {index === 0 && (
                          <div className='absolute inset-x-0 bottom-0 bg-black/60 py-0.5'>
                            <span className='text-[10px] font-medium text-white'>
                              {t('Original')}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right column - Image display */}
          <div
            className={clsxm(
              'relative flex items-center justify-center transition-all duration-300',
              showAnnotator || annotationMode === 'click'
                ? 'w-full h-full'
                : 'md:col-span-2',
            )}
          >
            <div
              className={clsxm(
                'relative bg-gray-50 dark:bg-gray-800 w-full flex items-center justify-center',
                showAnnotator || annotationMode === 'click'
                  ? 'h-full rounded-lg overflow-hidden'
                  : 'max-h-[calc(95vh-4rem)]',
              )}
            >
              {isCanvasMode ? (
                <Canvas
                  initialImage={currentImage}
                  onDraw={setMaskImage}
                  clearCanvas={clearCanvasCounter}
                />
              ) : editMode === 'visual' ? (
                <ClickToAnnotate
                  key={`visual-${annotationResetKey}-${currentImage}`}
                  imageUrl={currentImage}
                  isGenerating={mutation.isLoading || isPolling}
                  onComplete={() => {}}
                  onCancel={() => setEditMode('prompt')}
                  className='w-full h-full'
                  embedded={true}
                  onMarkersChange={(markers, hasValid) => {
                    setVisualMarkers(markers);
                    setHasValidAnnotations(hasValid);
                  }}
                />
              ) : annotationMode === 'click' ? (
                <ClickToAnnotate
                  imageUrl={currentImage}
                  isGenerating={mutation.isLoading || isPolling}
                  onComplete={(compiledPrompt) => {
                    // Trigger mutation with the compiled prompt
                    mutation.mutate({
                      prompt: compiledPrompt,
                      image: currentImage,
                      editModel: currentEditModel,
                      useHD: 'true',
                      aspectRatio: dimensions,
                      ...(additionalImages.length > 0 && { additionalImages }),
                      ...(resolution && { resolution }),
                    });
                    setAnnotationMode(null);
                  }}
                  onCancel={() => setAnnotationMode(null)}
                  className='w-full h-full'
                />
              ) : showAnnotator ? (
                <div className='relative w-full h-full'>
                  {/* ImageAnnotator Component */}
                  <div className='w-full h-full'>
                    <ImageAnnotator
                      imageUrl={currentImage}
                      onSave={(annotated, onComplete) => {
                        setAnnotatedImage(annotated);
                        setShowAnnotator(false);
                        toast.success(t('Changes marked successfully'));
                        onComplete();
                      }}
                      onCancel={() => {
                        setShowAnnotator(false);
                      }}
                      className='w-full h-full'
                    />
                  </div>
                </div>
              ) : (
                <div className='relative w-full h-full'>
                  <img
                    src={annotatedImage || currentImage}
                    alt='current image'
                    className='w-full h-full object-contain'
                    style={{ maxHeight: 'calc(95vh - 4rem)' }}
                  />
                  {/* Annotated Badge - Hidden on mobile */}
                  {annotatedImage && (
                    <div className='hidden sm:flex absolute top-4 right-4 items-center gap-2'>
                      <div className='bg-green-100 dark:bg-green-900/30 rounded-lg px-3 py-1.5 shadow-lg'>
                        <div className='flex items-center gap-2'>
                          <svg
                            className='h-4 w-4 text-green-600 dark:text-green-400'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                              clipRule='evenodd'
                            />
                          </svg>
                          <span className='text-xs font-medium text-green-800 dark:text-green-400'>
                            {t('Annotated')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowAnnotator(true)}
                        className='bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors'
                        title='Edit annotations'
                      >
                        <PencilAltIcon className='h-4 w-4 text-gray-600 dark:text-gray-400' />
                      </button>
                      <button
                        onClick={() => setAnnotatedImage(null)}
                        className='bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-colors'
                        title='Clear annotations'
                      >
                        <TrashIcon className='h-4 w-4 text-gray-600 dark:text-gray-400' />
                      </button>
                    </div>
                  )}

                  {/* Download button - bottom right corner */}
                  {imageHistory.length > 1 && selectedHistoryIndex > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(currentImage);
                          const blob = await response.blob();
                          const downloadUrl = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.style.display = 'none';
                          a.href = downloadUrl;
                          const extension =
                            currentImage.split('.').pop()?.toLowerCase() ||
                            'jpg';
                          a.download = `edited-image.${extension}`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(downloadUrl);
                        } catch (error) {
                          console.error('Error downloading image:', error);
                          window.open(currentImage, '_blank');
                        }
                      }}
                      className='absolute bottom-4 right-4 flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-all hover:shadow-xl'
                    >
                      <DownloadIcon className='h-4 w-4 text-gray-700 dark:text-gray-300' />
                      <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                        {t('Download')}
                      </span>
                    </button>
                  )}
                </div>
              )}
              {(mutation.isLoading || isPolling || isUploading) && (
                <div className='absolute inset-0 z-[999999] flex items-center justify-center bg-black bg-opacity-30'>
                  <div className='animate-in zoom-in flex w-40 flex-col items-center justify-center rounded-lg bg-white p-4 text-center shadow-lg'>
                    <Spinner className='animate-spin-fast z-50 h-8 text-primary-700' />
                    <p className='mt-2 text-center text-sm text-gray-600'>
                      {isPolling
                        ? t('Processing...')
                        : isUploading
                          ? t('Uploading...')
                          : t('Generating...')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </Modal>
  );
};

export default memo(EditModal);
